import {
  DC_Repositories_Base_Enumerations_BusinessUnit,
  DC_Repositories_Base_Enumerations_Label,
  RequestModels_UserAccounts_AccountCreationInitiatedEventData,
  RequestModels_UserAccounts_CanRegisterRequest,
  RequestModels_UserAccounts_PasswordResetInitiatedEventData,
  RequestModels_UserAccounts_RegisterRequest,
  ResponseModels_UserAccounts_CanRegisterV2ResponseModel,
} from '@monorepo-types/dc';

import { request } from '../client';
import type { ApiRequestConfig } from '../client/types';

type CanRegisterWithCustomerId = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  requestBody?: RequestModels_UserAccounts_CanRegisterRequest;
};
/**
 * Check if a customer can register, using a combination of customerId and address (NL) or surname (BE) data.
A validation exception will be thrown if either the address (for NL requests) or the surname (for BE requests) is not provided.
 * 
 * @returns ResponseModels_UserAccounts_CanRegisterV2ResponseModel Success
 */
export function canRegisterWithCustomerId(
  { businessUnit, label, customerId, requestBody }: CanRegisterWithCustomerId,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_UserAccounts_CanRegisterV2ResponseModel> {
  return request(
    {
      method: 'POST',
      path: `/dxpweb/public/${businessUnit}/${label}/useraccounts/customers/${customerId}/canregister`,
      body: requestBody,
      errors: {
        400: 'If a customer cannot be found by the customerId or if the address/surname does not match the found customer or            or either the address (for NL requests) or the surname (for BE requests) is not provided.',
      },
    },
    requestConfig,
  );
}

type Register = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  requestBody?: RequestModels_UserAccounts_RegisterRequest;
};
/**
 * Registers a customers' useraccount, using a combination of customerId and address data.
Also deletes the existing accounts with the same username + customerId when existing, after the CanRegister tests are verified.
 * 
 * @returns void 
 */
export function register(
  { businessUnit, label, customerId, requestBody }: Register,
  requestConfig: ApiRequestConfig = {},
): Promise<void> {
  return request(
    {
      method: 'PUT',
      path: `/dxpweb/public/${businessUnit}/${label}/useraccounts/customers/${customerId}/register`,
      body: requestBody,
      errors: { 400: 'If the registration process failed due to a functional error.', 409: 'Conflict' },
    },
    requestConfig,
  );
}

type RegisterPasswordResetInitiatedEvent = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  requestBody?: RequestModels_UserAccounts_PasswordResetInitiatedEventData;
};
/**
 * Registers a password reset initiated event along with the customer address data
 *
 * @returns void
 */
export function registerPasswordResetInitiatedEvent(
  { businessUnit, label, requestBody }: RegisterPasswordResetInitiatedEvent,
  requestConfig: ApiRequestConfig = {},
): Promise<void> {
  return request(
    {
      method: 'POST',
      path: `/dxpweb/public/${businessUnit}/${label}/useraccounts/register-password-reset-initiated-event`,
      body: requestBody,
    },
    requestConfig,
  );
}

type RegisterAccountCreationInitiatedEvent = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  requestBody?: RequestModels_UserAccounts_AccountCreationInitiatedEventData;
};
/**
 * Registers an account creation initiated event along with the customer address data
 *
 * @returns void
 */
export function registerAccountCreationInitiatedEvent(
  { businessUnit, label, requestBody }: RegisterAccountCreationInitiatedEvent,
  requestConfig: ApiRequestConfig = {},
): Promise<void> {
  return request(
    {
      method: 'POST',
      path: `/dxpweb/public/${businessUnit}/${label}/useraccounts/register-account-creation-initiated-event`,
      body: requestBody,
    },
    requestConfig,
  );
}
